import { onUnmounted, ref } from 'vue'
import { useTelemetry } from '@/features/telemetry/useTelemetry'

type DataPollingConfig<T = unknown> = {
	name: string
	getter: () => Promise<T | undefined>
	hasDataChanged: (newData: T | undefined, oldData: T | undefined) => boolean
	callback: (data: T | undefined) => void
	interval: number
	fetchOnStart?: boolean
}

export function useDataPolling<T = unknown>(config: DataPollingConfig<T>) {
	const { trackEvent } = useTelemetry()

	const data = ref<T>()
	let intervalId: number

	async function fetch() {
		const newData = await config.getter()

		if (config.hasDataChanged(newData, data.value)) {
			data.value = newData

			trackEvent('app', 'Polled data has changed', {
				name: config.name
			})

			config.callback(data.value)
		}
	}

	function start() {
		trackEvent('app', 'Starting data polling', {
			name: config.name
		})

		stop(true)

		intervalId = window.setInterval(fetch, config.interval)

		if (config.fetchOnStart) {
			fetch()
		}
	}

	function stop(dontLogItInTelemetry?: boolean) {
		if (!dontLogItInTelemetry) {
			trackEvent('app', 'Stopping data polling', {
				name: config.name
			})
		}

		clearInterval(intervalId)
	}

	onUnmounted(stop)

	return {
		start,
		stop
	}
}
